<template>
  <div class="admin-suggests-container fade-in">

    <!-- ── Page Header ───────────────────────────────────────────── -->
    <div class="page-header mb-4">
      <div class="page-header-left">
        <h2 class="page-title">Đề Xuất Sách</h2>
        <p class="page-subtitle">
          Quản lý và phê duyệt sách do người dùng đề xuất
          <span v-if="pendingCount > 0" class="pending-badge ms-2">{{ pendingCount }} chờ duyệt</span>
        </p>
      </div>
    </div>

    <!-- ── Filter Toolbar ─────────────────────────────────────────── -->
    <div class="filter-panel mb-4">
      <div class="filter-grid suggest-filter-grid">
        <!-- Search -->
        <div class="filter-field filter-field-search">
          <div class="search-box-panel">
            <input
              v-model="searchKeyword"
              type="text"
              placeholder="Tìm theo tên sách, tác giả..."
              class="panel-search-input"
            />
            <icon icon="fa-solid fa-magnifying-glass" class="panel-search-icon" />
          </div>
        </div>

        <!-- Status filter -->
        <div class="filter-field filter-custom-dropdown">
          <div
            class="custom-select-trigger"
            :class="{ 'dropdown-active': activeDropdown === 'status' }"
            @click.stop="toggleDropdown('status')"
          >
            <span class="trigger-text">{{ currentStatusLabel }}</span>
            <icon icon="fa-solid fa-chevron-down" class="trigger-arrow" />
          </div>
          <div v-if="activeDropdown === 'status'" class="custom-select-dropdown" @click.stop>
            <div class="dropdown-options-list">
              <div
                v-for="opt in statusOptions"
                :key="opt.value"
                class="dropdown-option-item"
                :class="{ 'option-selected': filterStatus === opt.value }"
                @click="selectStatus(opt.value)"
              >
                <span>{{ opt.label }}</span>
                <icon v-if="filterStatus === opt.value" icon="fa-solid fa-check" class="check-icon" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Table Panel ──────────────────────────────────────────── -->
    <div class="dashboard-panel">
      <div class="panel-header">
        <div class="panel-title-group">
          <h3 class="panel-title">Danh sách đề xuất</h3>
          <span class="panel-subtitle">Tổng số: {{ totalElements }} đề xuất</span>
        </div>
      </div>

      <!-- ── Pagination (Top) ────────────────────────────────── -->
      <div v-if="totalPages > 1" class="pagination-wrapper pagination-top">
        <div class="pagination-info">
          Hiển thị <strong>{{ currentPage * pageSize + 1 }}</strong> -
          <strong>{{ Math.min((currentPage + 1) * pageSize, totalElements) }}</strong>
          trong số <strong>{{ totalElements }}</strong> đề xuất
        </div>
        <div class="pagination-controls">
          <button
            @click="loadSuggests(currentPage - 1)"
            :disabled="currentPage === 0"
            class="btn-pagination"
            title="Trang trước"
          >
            <icon icon="fa-solid fa-chevron-left" />
          </button>
          <button
            v-for="p in visiblePages"
            :key="p"
            @click="loadSuggests(p)"
            class="btn-pagination"
            :class="{ active: currentPage === p }"
          >
            {{ p + 1 }}
          </button>
          <button
            @click="loadSuggests(currentPage + 1)"
            :disabled="currentPage === totalPages - 1"
            class="btn-pagination"
            title="Trang sau"
          >
            <icon icon="fa-solid fa-chevron-right" />
          </button>
        </div>
      </div>

      <div class="table-responsive-wrapper">
        <table class="custom-admin-table">
          <thead>
            <tr>
              <th class="text-center col-stt">#</th>
              <th>Tên sách đề xuất</th>
              <th>Tác giả</th>
              <th>Người đề xuất</th>
              <th class="text-center">Ngày gửi</th>
              <th class="text-center col-status">Trạng thái</th>
              <th class="text-center col-actions">Hành động</th>
            </tr>
          </thead>
          <tbody>
            <!-- Skeleton Loader -->
            <template v-if="isLoading">
              <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                <td class="text-center">
                  <span class="skeleton-bar skeleton-text-stt"></span>
                </td>
                <td>
                  <span class="skeleton-bar" style="width: 140px; height: 16px;"></span>
                </td>
                <td>
                  <span class="skeleton-bar" style="width: 100px; height: 16px;"></span>
                </td>
                <td>
                  <span class="skeleton-bar" style="width: 110px; height: 16px;"></span>
                </td>
                <td class="text-center">
                  <span class="skeleton-bar" style="width: 60px; height: 16px;"></span>
                </td>
                <td class="text-center">
                  <span class="skeleton-bar skeleton-badge"></span>
                </td>
                <td class="text-center">
                  <div class="skeleton-actions">
                    <span class="skeleton-btn"></span>
                    <span class="skeleton-btn"></span>
                    <span class="skeleton-btn"></span>
                  </div>
                </td>
              </tr>
            </template>

            <!-- Data Rows -->
            <template v-else>
              <tr
                v-for="(item, idx) in filteredSuggests"
                :key="item.id"
                class="table-row-hover"
              >
                <td class="col-stt text-center text-muted fw-semibold">{{ currentPage * pageSize + idx + 1 }}</td>

                <td>
                  <div class="book-name-cell">
                    <span class="row-name">{{ item.bookName }}</span>
                    <p v-if="item.bookContent" class="text-muted m-0 fs-8 truncate-1">{{ item.bookContent }}</p>
                  </div>
                </td>

                <td class="text-muted">{{ item.authorName || '—' }}</td>

                <td>
                  <div class="suggester-cell">
                    <span class="fw-semibold text-dark">{{ item.suggesterName }}</span>
                    <span class="fs-8 text-muted d-block">@{{ item.suggesterUsername }}</span>
                  </div>
                </td>

                <td class="text-center">
                  <span class="fs-8 text-muted">{{ formatDateTime(item.createTime) }}</span>
                </td>

                <td class="text-center">
                  <span :class="['status-badge-custom', getStatusBadgeClass(item.status)]">
                    {{ getStatusLabel(item.status) }}
                  </span>
                </td>

                <td class="text-center">
                  <div class="action-btn-group">
                    <button @click="openDetailModal(item)" class="btn-action-icon btn-action-view" data-tooltip="Xem chi tiết">
                      <icon icon="fa-solid fa-eye" />
                    </button>
                    <button
                      v-if="item.status === 0"
                      @click="openApproveModal(item)"
                      class="btn-action-icon btn-action-edit"
                      data-tooltip="Duyệt & Tạo sách"
                    >
                      <icon icon="fa-solid fa-circle-check" />
                    </button>
                    <button
                      v-if="item.status === 0"
                      @click="openRejectModal(item)"
                      class="btn-action-icon btn-action-delete"
                      data-tooltip="Từ chối"
                    >
                      <icon icon="fa-solid fa-ban" />
                    </button>
                  </div>
                </td>
              </tr>

              <tr v-if="filteredSuggests.length === 0">
                <td colspan="7" class="empty-state-row">
                  <icon icon="fa-solid fa-paper-plane" class="empty-icon" />
                  <span>Không có đề xuất nào</span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- ── Pagination ──────────────────────────────────────── -->
      <div v-if="totalPages > 1" class="pagination-wrapper">
        <div class="pagination-info">
          Hiển thị <strong>{{ currentPage * pageSize + 1 }}</strong> -
          <strong>{{ Math.min((currentPage + 1) * pageSize, totalElements) }}</strong>
          trong số <strong>{{ totalElements }}</strong> đề xuất
        </div>
        <div class="pagination-controls">
          <button
            @click="loadSuggests(currentPage - 1)"
            :disabled="currentPage === 0"
            class="btn-pagination"
            title="Trang trước"
          >
            <icon icon="fa-solid fa-chevron-left" />
          </button>
          <button
            v-for="p in visiblePages"
            :key="p"
            @click="loadSuggests(p)"
            class="btn-pagination"
            :class="{ active: currentPage === p }"
          >
            {{ p + 1 }}
          </button>
          <button
            @click="loadSuggests(currentPage + 1)"
            :disabled="currentPage === totalPages - 1"
            class="btn-pagination"
            title="Trang sau"
          >
            <icon icon="fa-solid fa-chevron-right" />
          </button>
        </div>
      </div>

    </div>

    <!-- ══ DETAIL MODAL ═══════════════════════════════════════════ -->
    <teleport to="body">
      <div v-if="detailModalVisible && selectedSuggest" class="modal-backdrop-custom">
        <div class="modal-card-custom" style="max-width: 620px;">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom">
            <h3 class="modal-title-custom">
              <icon icon="fa-solid fa-circle-info" />
              Chi Tiết Đề Xuất
              <span :class="['status-badge-custom ms-2', getStatusBadgeClass(selectedSuggest.status)]">
                {{ getStatusLabel(selectedSuggest.status) }}
              </span>
            </h3>
            <button @click="closeDetailModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-body-custom">
            <!-- Book info -->
            <div class="detail-section mb-3">
              <h5 class="detail-section-title">Thông tin sách đề xuất</h5>
              <dl class="detail-dl">
                <dt>Tên sách</dt><dd class="fw-bold text-primary">{{ selectedSuggest.bookName }}</dd>
                <dt>Tác giả</dt><dd>{{ selectedSuggest.authorName || '—' }}</dd>
                <dt>Người đề xuất</dt>
                <dd>{{ selectedSuggest.suggesterName }} <span class="text-muted fs-8">(@{{ selectedSuggest.suggesterUsername }})</span></dd>
                <dt>Mô tả / Tóm tắt</dt>
                <dd>
                  <span v-if="selectedSuggest.bookContent" class="text-secondary">{{ selectedSuggest.bookContent }}</span>
                  <span v-else class="text-muted">—</span>
                </dd>
              </dl>
            </div>



            <!-- Admin note if rejected -->
            <div v-if="selectedSuggest.status === 2 && selectedSuggest.adminNote" class="delete-warn-text">
              <icon icon="fa-solid fa-circle-info" />
              <span><strong>Lý do từ chối:</strong> {{ selectedSuggest.adminNote }}</span>
            </div>
          </div>

          <div class="modal-footer-custom">
            <button @click="closeDetailModal" class="btn-modal-cancel">Đóng lại</button>
            <button
              v-if="selectedSuggest.status === 0"
              @click="openApproveFromDetail"
              class="btn-modal-confirm"
            >
              <icon icon="fa-solid fa-circle-check" class="me-1" />Duyệt & Tạo sách
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ══ APPROVE MODAL (Create Book Form prefilled) ═══════════════ -->
    <teleport to="body">
      <div v-if="approveModalVisible && selectedSuggest" class="modal-backdrop-custom approve-modal">
        <div class="modal-card-custom modal-card-lg">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom">
            <h3 class="modal-title-custom">
              <icon icon="fa-solid fa-circle-check" />
              Duyệt Đề Xuất & Tạo Sách
            </h3>
            <button @click="closeApproveModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-body-custom">
            <p class="text-muted-dark mb-3 fs-8">
              Đề xuất từ: <strong>{{ selectedSuggest.suggesterName }}</strong> - Sách: <strong>{{ selectedSuggest.bookName }}</strong>
            </p>

            <!-- Info banner -->
            <div class="delete-warn-text mb-4" style="color: var(--primary); border-color: rgba(30, 62, 63, 0.15); background-color: rgba(30, 62, 63, 0.04);">
              <icon icon="fa-solid fa-circle-info" class="flex-shrink-0" />
              <span>Điền thông tin sách bên dưới. Sau khi tạo sách, hệ thống sẽ tự động chuyển <strong>đánh giá, review và tiến độ</strong> của người đề xuất sang sách mới.</span>
            </div>

            <!-- Prefill suggestion reference -->
            <div class="suggest-prefill-banner d-flex gap-3 align-items-center p-3 mb-4 rounded-3 border">
              <icon icon="fa-solid fa-lightbulb" class="text-warning flex-shrink-0" />
              <div class="fs-8">
                <strong>Gợi ý từ đề xuất:</strong> &nbsp;
                <span class="fw-bold text-primary">{{ selectedSuggest.bookName }}</span>
                <span v-if="selectedSuggest.authorName"> — {{ selectedSuggest.authorName }}</span>
              </div>
            </div>

            <!-- Book Form -->
            <form id="approveBookForm" @submit.prevent="submitApprove">
              <div class="book-modal-grid">

                <!-- Left: Cover -->
                <div class="book-modal-left">
                  <div
                    class="cover-preview-box clickable-preview"
                    :class="{ 'drag-over': dragOver, 'has-preview': bookForm.coverPreview }"
                    @dragover.prevent="dragOver = true"
                    @dragleave.prevent="dragOver = false"
                    @drop.prevent="onCoverDrop($event)"
                    @click="$refs.coverFileInput.click()"
                    title="Nhấp hoặc kéo thả ảnh bìa vào đây"
                  >
                    <img v-if="bookForm.coverPreview" :src="bookForm.coverPreview" class="cover-preview-img" alt="Ảnh bìa" />
                    <div v-else class="cover-preview-placeholder">
                      <icon icon="fa-solid fa-cloud-arrow-up" />
                      <span>Chọn ảnh hoặc kéo thả vào đây</span>
                    </div>
                    <input ref="coverFileInput" type="file" accept="image/*" class="d-none" @change="onCoverFileChange" />
                  </div>
                  <div class="form-field mt-3 text-center">
                    <span class="text-muted fs-8">Kích thước tối đa 2MB (Định dạng ảnh)</span>
                  </div>
                </div>

                <!-- Right: Detail Fields -->
                <div class="book-modal-right">
                  <div class="form-field mb-3">
                    <label class="form-field-label">Tên sách <span class="required-mark">*</span></label>
                    <input
                      v-model="bookForm.name"
                      type="text"
                      class="custom-input-modal"
                      :class="{ 'input-error': bookFormErrors.name }"
                      placeholder="Nhập tên đầu sách..."
                      ref="approveBookNameInput"
                      maxlength="500"
                    />
                    <span v-if="bookFormErrors.name" class="field-error-msg">{{ bookFormErrors.name }}</span>
                  </div>

                  <div class="form-field mb-3">
                    <label class="form-field-label">Tóm tắt nội dung <span class="required-mark">*</span></label>
                    <textarea
                      v-model="bookForm.content"
                      class="custom-textarea-modal"
                      :class="{ 'input-error': bookFormErrors.content }"
                      rows="3"
                      placeholder="Viết tóm tắt ngắn (tối thiểu 10 ký tự)..."
                    ></textarea>
                    <span v-if="bookFormErrors.content" class="field-error-msg">{{ bookFormErrors.content }}</span>
                  </div>

                  <div class="form-field mb-4">
                    <label class="form-field-label">Link Shopee Affiliate</label>
                    <input
                      v-model="bookForm.linkShopee"
                      type="text"
                      class="custom-input-modal"
                      placeholder="https://shopee.vn/..."
                    />
                  </div>

                  <!-- Relations -->
                  <div class="relations-section">
                    <h4 class="relations-section-title">Gán mối quan hệ</h4>
                    <div class="relations-grid">

                      <!-- Authors -->
                      <div class="relation-card relation-card-full" :class="{ 'card-error': bookFormErrors.authors }">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="relation-card-label m-0">
                            <icon icon="fa-solid fa-user-pen" class="me-1" />Tác giả <span class="required-mark">*</span>
                          </span>
                        </div>

                        <!-- Selected Chips -->
                        <div v-if="bookForm.selectedAuthors.length > 0" class="selected-relations-chips mb-2">
                          <span v-for="a in bookForm.selectedAuthors" :key="a.id" class="relation-chip" @click="removeAuthor(a)" title="Nhấp để bỏ chọn">
                            {{ a.name }}<template v-if="a.yob || a.yod"> ({{ a.yob || '?' }} - {{ a.yod || '?' }})</template>
                            <icon icon="fa-solid fa-xmark" class="ms-1 chip-remove-icon" />
                          </span>
                        </div>

                        <div class="relation-search-wrapper mb-2" style="max-width: 280px;">
                          <input type="text" v-model="searchAuthorModal" class="relation-search-input" placeholder="Tìm tác giả..." />
                        </div>
                        <div class="checklist-box checklist-grid">
                          <label v-for="a in sortedAuthors" :key="a.id" class="checklist-item">
                            <input type="checkbox" class="check-input" @change="toggleAuthor(a)" />
                            <span>{{ a.name }}<template v-if="a.yob || a.yod"> ({{ a.yob || '?' }} - {{ a.yod || '?' }})</template></span>
                          </label>
                          <span v-if="!sortedAuthors.length" class="checklist-empty">Không còn tác giả nào để chọn</span>
                        </div>
                        <span v-if="bookFormErrors.authors" class="field-error-msg mt-1 d-block">{{ bookFormErrors.authors }}</span>
                      </div>

                      <!-- Translators -->
                      <div class="relation-card relation-card-full">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="relation-card-label m-0">
                            <icon icon="fa-solid fa-language" class="me-1" />Dịch giả
                          </span>
                        </div>

                        <!-- Selected Chips -->
                        <div v-if="bookForm.selectedTranslators.length > 0" class="selected-relations-chips mb-2">
                          <span v-for="t in bookForm.selectedTranslators" :key="t.id" class="relation-chip" @click="removeTranslator(t)" title="Nhấp để bỏ chọn">
                            {{ t.name }}
                            <icon icon="fa-solid fa-xmark" class="ms-1 chip-remove-icon" />
                          </span>
                        </div>

                        <div class="relation-search-wrapper mb-2" style="max-width: 280px;">
                          <input type="text" v-model="searchTranslatorModal" class="relation-search-input" placeholder="Tìm dịch giả..." />
                        </div>
                        <div class="checklist-box checklist-grid">
                          <label v-for="t in sortedTranslators" :key="t.id" class="checklist-item">
                            <input type="checkbox" class="check-input" @change="toggleTranslator(t)" />
                            <span>{{ t.name }}</span>
                          </label>
                          <span v-if="!sortedTranslators.length" class="checklist-empty">Không còn dịch giả nào để chọn</span>
                        </div>
                      </div>

                      <!-- Categories -->
                      <div class="relation-card relation-card-full" :class="{ 'card-error': bookFormErrors.categories }">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <span class="relation-card-label m-0">
                            <icon icon="fa-solid fa-tags" class="me-1" />Thể loại <span class="required-mark">*</span>
                          </span>
                        </div>

                        <!-- Selected Chips -->
                        <div v-if="bookForm.selectedCategories.length > 0" class="selected-relations-chips mb-2">
                          <span v-for="c in bookForm.selectedCategories" :key="c.id" class="relation-chip" @click="removeCategory(c)" title="Nhấp để bỏ chọn">
                            {{ c.name }}
                            <icon icon="fa-solid fa-xmark" class="ms-1 chip-remove-icon" />
                          </span>
                        </div>

                        <div class="relation-search-wrapper mb-2" style="max-width: 280px;">
                          <input type="text" v-model="searchCategoryModal" class="relation-search-input" placeholder="Tìm thể loại..." />
                        </div>
                        <div class="checklist-box checklist-grid">
                          <label v-for="c in sortedCategories" :key="c.id" class="checklist-item">
                            <input type="checkbox" class="check-input" @change="toggleCategory(c)" />
                            <span>{{ c.name }}</span>
                          </label>
                          <span v-if="!sortedCategories.length" class="checklist-empty">Không còn thể loại nào để chọn</span>
                        </div>
                        <span v-if="bookFormErrors.categories" class="field-error-msg mt-1 d-block">{{ bookFormErrors.categories }}</span>
                      </div>

                    </div>
                  </div>
                </div>

              </div>
            </form>
          </div>

          <div class="modal-footer-custom">
            <button @click="closeApproveModal" class="btn-modal-cancel" :disabled="isSubmitting">Hủy bỏ</button>
            <button @click="submitApprove" class="btn-modal-confirm" :disabled="isSubmitting">
              <icon v-if="isSubmitting" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              <icon v-else icon="fa-solid fa-plus" class="me-1" />
              {{ isSubmitting ? 'Đang tạo sách...' : 'Tạo sách & Duyệt đề xuất' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ══ REJECT MODAL ═══════════════════════════════════════════ -->
    <teleport to="body">
      <div v-if="rejectModalVisible && selectedSuggest" class="modal-backdrop-custom">
        <div class="modal-card-custom modal-card-sm">
          <div class="modal-sheet-handle"></div>
          <div class="modal-header-custom modal-header-danger">
            <h3 class="modal-title-custom modal-title-danger">
              <icon icon="fa-solid fa-ban" />
              Từ Chối Đề Xuất
            </h3>
            <button @click="closeRejectModal" class="btn-close-modal" aria-label="Đóng">
              <icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="modal-body-custom">
            <p class="text-muted-dark mb-3">
              Bạn đang từ chối đề xuất sách: <strong>{{ selectedSuggest.bookName }}</strong>
            </p>
            <div class="form-field">
              <label class="form-field-label">Lý do từ chối (không bắt buộc)</label>
              <textarea
                v-model="rejectNote"
                class="custom-textarea-modal"
                rows="3"
                placeholder="Ví dụ: Sách này đã có trong hệ thống, vui lòng tìm kiếm trước..."
              ></textarea>
            </div>
          </div>
          <div class="modal-footer-custom">
            <button @click="closeRejectModal" class="btn-modal-cancel" :disabled="isSubmitting">Hủy bỏ</button>
            <button @click="submitReject" class="btn-modal-danger" :disabled="isSubmitting">
              <icon v-if="isSubmitting" icon="fa-solid fa-spinner" class="fa-spin me-1" />
              {{ isSubmitting ? 'Đang xử lý...' : 'Xác nhận từ chối' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

  </div>
</template>

<script src="./book-suggests.js"></script>
<style scoped src="./book-suggests.css"></style>
